import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, Rolle, RolleAnlegen } from "@kundeninfo/api-client";
import { darfAbbrechen, Feld, Modal, ModalFuss, useBeforeUnload,
         useFormularZustand } from "@kundeninfo/ui";
import { Formular, Hinweis, Schalter } from "@kundeninfo/ui/me-design";
import { KEINE_ZAHL, Meldung, alsZahl, eingabeUnlesbar, meldungAus } from "./gemeinsam.js";
import "../Verwaltung.css";

/**
 * Der Verwaltungsbereich ANSPRECHPARTNER-ROLLEN.
 *
 * Herausgelöst aus views/Verwaltung.tsx (Etappe 2, Aufgabe 3) — rein strukturell, CSS und
 * Verhalten unverändert (Block `vw__`, views/Verwaltung.css).
 *
 * **Ohne zweite Ebene** (Entscheid Aufgabe 3, im Einklang mit der Spezifikation, die die
 * Rollen ausdrücklich als Formular-Bereich nennt): Eine Rolle trägt drei Felder — Name,
 * Reihenfolge, Aktiv —, und alle drei stehen bereits in ihrer Listenzeile. Eine Spaltenliste
 * davor brächte nichts zu sehen, was die Liste nicht schon zeigt, und nähme die eine
 * Auskunft weg, um die es hier geht: die REIHENFOLGE über alle Rollen hinweg.
 */

/** Nur die drei Wege dieses Bereichs — so lässt er sich im Test mit drei vi.fn zeichnen. */
export type RollenClient = Pick<ApiClient,
  "listContactRoles" | "createContactRole" | "updateContactRole">;

type RollenDialog = {
  id: string | null;
  version: number;
  name: string;
  sortIndex: string;
  active: boolean;
};

const LEERER_ROLLENDIALOG: RollenDialog =
  { id: null, version: 0, name: "", sortIndex: "0", active: true };

type RollenFeld = "name" | "sortIndex";

const ROLLEN_FELDER: readonly RollenFeld[] = ["name", "sortIndex"];

/** Wie konditionsdialogAus, für die Rollen. */
function rollendialogAus(r: Rolle | null): RollenDialog {
  if (r === null) return LEERER_ROLLENDIALOG;
  return { id: r.id, version: r.version, name: r.name, sortIndex: String(r.sortIndex),
           active: r.active };
}

function rollendialogGleich(a: RollenDialog, b: RollenDialog): boolean {
  return a.id === b.id && a.version === b.version && a.name === b.name
    && a.sortIndex === b.sortIndex && a.active === b.active;
}

export function RollenBereich({ client }: { client: RollenClient }): ReactElement {
  const [zeilen, setZeilen] = useState<Rolle[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [dialog, setDialog] = useState<RollenDialog | null>(null);
  /** Eine Auskunft nach „Neu laden“: Die geöffnete Rolle gibt es nicht mehr. */
  const [auskunft, setAuskunft] = useState<string | null>(null);

  const listContactRoles = client.listContactRoles;

  useEffect(() => {
    let abgebrochen = false;
    listContactRoles()
      .then((antwort) => { if (!abgebrochen) setZeilen(antwort.items); })
      .catch((fehler: unknown) => {
        if (!abgebrochen) {
          setLadeFehler(meldungAus(fehler, "Die Rollen konnten nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [listContactRoles]);

  /**
   * Formularzustände der Maske (ME-Etappe 3, Aufgabe 6): `laeuft` sperrt Felder und Fuß,
   * eine 422 (auch die eigene „keine Zahl") hängt am Feld und der Fokus springt dorthin,
   * eine 409 (die Rolle trägt eine Version) hält die Eingaben als „fremder Stand" fest —
   * „Neu laden" holt die Liste und setzt die Maske auf den Serverstand.
   */
  const formular = useFormularZustand<true>({
    speichern: async () => {
      const d = dialog;
      if (d === null) throw new Error("Keine Rolle offen.");
      const eingabe = eingabeAus(d);
      if (d.id === null) {
        // Anlegen antwortet nur mit der Kennung — die Liste neu lesen (sie ist zudem
        // nach sortIndex geordnet, und der neue Eintrag gehört an seinen Platz).
        await client.createContactRole(eingabe);
        const antwort = await client.listContactRoles();
        setZeilen(antwort.items);
      } else {
        const neu = await client.updateContactRole(d.id, d.version, eingabe);
        setZeilen((alt) => (alt === null ? alt
          : alt.map((x) => (x.id === neu.id ? neu : x))
              .sort((a, b) => a.sortIndex - b.sortIndex)));
      }
      return true;
    },
    neuLaden: async () => {
      const antwort = await client.listContactRoles();
      setZeilen(antwort.items);
      const d = dialog;
      if (d === null || d.id === null) return;
      const frisch = antwort.items.find((z) => z.id === d.id);
      if (frisch === undefined) {
        // Inzwischen gelöscht (Review A6): NICHT still zu „Neue Rolle“ werden — die Maske
        // schliesst und sagt, was geschehen ist (Muster KundeNotizen).
        setDialog(null);
        setAuskunft("Diese Rolle wurde inzwischen gelöscht.");
        return;
      }
      setDialog(rollendialogAus(frisch));
    },
    bekannteFelder: ROLLEN_FELDER,
    beschriftungen: { name: "Name", sortIndex: "Reihenfolge" },
  });
  const arbeitet = formular.laeuft;
  const feldFehler = formular.feldFehler as Partial<Record<RollenFeld, string>>;

  // 2a-Kleinpunkt, wie im Konditionsbereich.
  const dialogGeaendert = dialog !== null && !rollendialogGleich(
    dialog, rollendialogAus(zeilen?.find((z) => z.id === dialog.id) ?? null));
  useBeforeUnload(dialogGeaendert);

  /** Der eine Weg aus dem Modal neben dem Speichern (Hausregel „striktes Modalpanel",
   *  06.09.2026) — mit Rückfrage, wenn etwas Ungespeichertes darin steht. Escape ruft
   *  denselben Weg (siehe `Modal` in @kundeninfo/ui); ein Klick daneben schliesst nicht. */
  async function abbrechen(): Promise<void> {
    // Kein Ausgang, solange geschrieben wird — das gilt für die ESCAPE-Taste genauso wie
    // für den (dann gesperrten) Knopf: Beide gehen durch diese Funktion, und die Antwort
    // des Servers braucht ihr Panel noch (Fixrunde 1).
    if (arbeitet) return;
    if (!(await darfAbbrechen(dialogGeaendert))) return;
    setDialog(null);
  }

  function oeffnen(r: Rolle | null): void {
    formular.zuruecksetzen();
    setAuskunft(null);
    setDialog(rollendialogAus(r));
  }

  /** Der Rumpf — wirft die eigene 422, wenn die Reihenfolge keine Zahl ist. */
  function eingabeAus(d: RollenDialog): RolleAnlegen {
    const sortIndex = alsZahl(d.sortIndex);
    if (sortIndex === KEINE_ZAHL || sortIndex === null) {
      throw eingabeUnlesbar([{ field: "sortIndex", message: "Bitte eine Zahl eintragen." }]);
    }
    return { name: d.name, sortIndex, active: d.active };
  }

  async function speichern(): Promise<void> {
    if (await formular.aktion() === true) setDialog(null);
  }

  if (ladeFehler !== null) return <Meldung text={ladeFehler} />;
  if (zeilen === null) return <p className="vw__hinweis">Lädt …</p>;

  // Die Liste kommt bereits nach sortIndex geordnet; hier wird sie es NOCH EINMAL, damit
  // die Reihenfolge auch nach einem Ändern ohne Neuladen stimmt.
  const sortiert = [...zeilen].sort((a, b) => a.sortIndex - b.sortIndex);

  return (
    <>
      {auskunft !== null && <Hinweis art="info">{auskunft}</Hinweis>}
      <div className="vw__leiste">
        <button type="button" className="vw__primaer" onClick={() => oeffnen(null)}>
          Neue Rolle
        </button>
      </div>

      <ul className="vw__liste">
        {sortiert.map((r) => (
          <li key={r.id} className="vw__eintrag">
            <div className="vw__eintrag-kopf">
              <span className="vw__eintrag-name">{r.name}</span>
              {!r.active && <span className="vw__marke">stillgelegt</span>}
            </div>
            <span className="vw__eintrag-zusatz">Reihenfolge {r.sortIndex}</span>
            <div className="vw__eintrag-aktionen">
              <button type="button" className="vw__knopf" aria-label={`${r.name} ändern`}
                onClick={() => oeffnen(r)}>
                Ändern
              </button>
            </div>
          </li>
        ))}
      </ul>

      {dialog !== null && (
        <Modal titel={dialog.id === null ? "Neue Rolle" : "Rolle ändern"}
          onAbbrechen={abbrechen} abbrechenGesperrt={arbeitet}>
          {formular.meldung !== null && <Meldung text={formular.meldung} />}
          <Formular beschriftung="oben" {...formular.formularProps}
            hinweis={formular.zustand === "fremder-stand" ? (
              <span>
                Diese Rolle wurde inzwischen geändert. Deine Eingaben stehen noch da —
                „Neu laden" übernimmt den aktuellen Stand.
              </span>
            ) : undefined}>
            <Feld id="vw-r-name" label="Name" bearbeitbar wert={dialog.name}
              onChange={(w) => setDialog((d) => (d === null ? d : { ...d, name: w }))}
              {...(feldFehler.name !== undefined ? { fehler: feldFehler.name } : {})} />
            <Feld id="vw-r-sortIndex" label="Reihenfolge" bearbeitbar wert={dialog.sortIndex}
              onChange={(w) => setDialog((d) => (d === null ? d : { ...d, sortIndex: w }))}
              {...(feldFehler.sortIndex !== undefined
                ? { fehler: feldFehler.sortIndex } : {})} />
            {/* Echtes Ja/Nein → `Schalter` (me-schalter) statt des rohen Kästchens. */}
            <Schalter id="vw-r-active" beschriftung="Aktiv (steht zur Auswahl)"
              an={dialog.active}
              onGeaendert={(e) => setDialog((d) => (d === null ? d
                : { ...d, active: e.an }))} />
          </Formular>
          <ModalFuss speichernText="Speichern"
            speichernGesperrt={formular.zustand !== "bereit"}
            abbrechenGesperrt={arbeitet}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </>
  );
}
